import * as React from 'react';
import type { Message, AudioChatResponse } from '@/types';
import { MessageSender } from '@/types';

import { BASE_URL } from '@/api/client';
import { parseSSELines } from '@/services/chatService';
import type { SSEEvent } from '@/services/chatService';
import { reportError } from '@/utils/reportError';

interface SSEEventData {
  content?: string;
  message_id?: string;
  session_id?: string;
  created_at?: string;
  suggested_questions?: Array<{ id: string; text: string }>;
  detail?: string;
  audio_url?: string;
  chunk?: string;
  audio?: string;
}

interface UsePublicChatOptions {
  echoId?: string;
  echoName: string;
  pageSlug?: string;
  ttsProvider?: 'azure' | 'elevenlabs' | null;
}

interface PublicEchoInfo {
  id: string;
  name: string;
  domain?: string;
  category?: string;
  bio?: string;
  profile_image_url?: string;
}

interface CustomPublicPageInfo {
  id: string;
  slug: string;
  echo: PublicEchoInfo;
}

interface UsePublicChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  // Add audio message sender
  sendAudioMessage: (blob: Blob, waveform: number[], duration: number) => Promise<void>;
  sessionId: string;
  echoInfo: PublicEchoInfo | null;
  isEchoLoading: boolean;
  // Audio playback
  playAudio: (messageId: string, audioUrl: string) => void;
  stopAudio: () => void;
  currentlyPlayingId: string | null;
  isAudioLoading: boolean;
}

/** Manages anonymous public chat sessions with an echo, including SSE streaming and audio playback. */
export function usePublicChat({ echoId, pageSlug }: UsePublicChatOptions): UsePublicChatReturn {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionId] = React.useState(() => crypto.randomUUID());
  const [hasGreeted, setHasGreeted] = React.useState(false);
  const [echoInfo, setEchoInfo] = React.useState<PublicEchoInfo | null>(null);
  const [isEchoLoading, setIsEchoLoading] = React.useState(!!(echoId || pageSlug));

  // Audio playback state
  const [currentlyPlayingId, setCurrentlyPlayingId] = React.useState<string | null>(null);
  const [isAudioLoading, setIsAudioLoading] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const audioChunksRef = React.useRef<Map<string, string[]>>(new Map());

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const localAudioUrlsRef = React.useRef<Set<string>>(new Set());
  const ttsProvider = 'elevenlabs';

  // Fetch echo info on mount
  React.useEffect(() => {
    async function fetchEchoInfo() {
      setIsEchoLoading(true);
      try {
        const metadataPath = pageSlug
          ? `/public/pages/${encodeURIComponent(pageSlug)}`
          : echoId
            ? `/public/echo/${encodeURIComponent(echoId)}`
            : null;
        if (!metadataPath) {
          setEchoInfo(null);
          return;
        }

        const response = await fetch(`${BASE_URL}${metadataPath}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const data = (await response.json()) as PublicEchoInfo | CustomPublicPageInfo;
        setEchoInfo('echo' in data ? data.echo : data);
      } catch (err) {
        reportError('usePublicChat.fetchEchoInfo', err);
        setError('Could not load this Echo right now. Please refresh and try again.');
      } finally {
        setIsEchoLoading(false);
      }
    }
    if (echoId || pageSlug) fetchEchoInfo();
    else setIsEchoLoading(false);
  }, [echoId, pageSlug]);

  // Trigger greeting on mount
  React.useEffect(() => {
    const hasConversationTarget = pageSlug ? !!echoInfo : !!echoId;
    if (!hasGreeted && hasConversationTarget) {
      setHasGreeted(true);
      sendMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [echoId, echoInfo, hasGreeted, pageSlug]);

  React.useEffect(() => {
    const localAudioUrls = localAudioUrlsRef.current;
    return () => {
      abortControllerRef.current?.abort();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      localAudioUrls.forEach((url) => URL.revokeObjectURL(url));
      localAudioUrls.clear();
    };
  }, []);

  const sendMessage = React.useCallback(
    async (content: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setError(null);
      setIsLoading(true);

      // 1. Optimistic User Message (Skip if greeting)
      if (content.trim()) {
        const userMessage: Message = {
          id: `user-${Date.now()}`,
          content,
          sender_type: MessageSender.USER,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      // 2. AI Placeholder
      const aiTempId = `ai-${Date.now()}`;
      const aiPlaceholder: Message = {
        id: aiTempId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        isTextComplete: false,
        audioBuffer: [],
        has_audio: true,
      };
      setMessages((prev) => [...prev, aiPlaceholder]);

      try {
        const queryParams = [
          `content=${encodeURIComponent(content)}`,
          `session_id=${encodeURIComponent(sessionId)}`,
          `tts_provider=${encodeURIComponent(ttsProvider)}`,
          `generate_audio=true`,
        ].join('&');

        const streamPath = pageSlug
          ? `/public/pages/${encodeURIComponent(pageSlug)}/stream`
          : echoId
            ? `/public/stream/${encodeURIComponent(echoId)}`
            : null;
        if (!streamPath) throw new Error('No public chat target available');

        const url = `${BASE_URL}${streamPath}?${queryParams}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader available');

        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let accumulatedText = '';

        const handleTextChunk = (data: SSEEventData) => {
          accumulatedText += data?.content || '';
          setMessages((prev) =>
            prev.map((msg) => (msg.id === aiTempId ? { ...msg, content: accumulatedText } : msg))
          );
        };

        const handleAudioChunk = (data: SSEEventData, rawData: unknown) => {
          const chunk =
            data?.chunk || data?.audio || (typeof rawData === 'string' ? rawData : null);
          if (!chunk) return;
          const chunks = audioChunksRef.current.get(aiTempId) || [];
          chunks.push(chunk);
          audioChunksRef.current.set(aiTempId, chunks);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiTempId
                ? { ...msg, audioBuffer: [...(msg.audioBuffer || []), chunk] }
                : msg
            )
          );
        };

        const handleMessageEnd = (data: SSEEventData) => {
          const finalContent = data?.content || accumulatedText;
          const audioUrl = data?.audio_url;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiTempId
                ? {
                    ...msg,
                    content: finalContent,
                    isTextComplete: true,
                    ...(audioUrl ? { audio_url: audioUrl } : {}),
                    ...(data?.suggested_questions
                      ? { suggested_questions: data.suggested_questions }
                      : {}),
                  }
                : msg
            )
          );
          setIsLoading(false);
        };

        const handleSSEEvent = (evt: SSEEvent) => {
          const data = evt.data as SSEEventData;
          switch (evt.event) {
            case 'text_chunk':
              handleTextChunk(data);
              break;
            case 'text_complete':
              accumulatedText = data?.content || accumulatedText;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiTempId
                    ? {
                        ...msg,
                        content: accumulatedText,
                        isTextComplete: true,
                        ...(data?.suggested_questions
                          ? { suggested_questions: data.suggested_questions }
                          : {}),
                      }
                    : msg
                )
              );
              break;
            case 'audio_start':
              setIsAudioLoading(true);
              audioChunksRef.current.set(aiTempId, []);
              break;
            case 'audio_chunk':
              handleAudioChunk(data, evt.data);
              break;
            case 'audio_end':
              setIsAudioLoading(false);
              setMessages((prev) =>
                prev.map((msg) => (msg.id === aiTempId ? { ...msg, has_audio: true } : msg))
              );
              break;
            case 'audio_error':
              setIsAudioLoading(false);
              break;
            case 'message_end':
              handleMessageEnd(data);
              break;
            case 'error':
              setError(data?.detail || 'Stream Error');
              setMessages((prev) => prev.filter((msg) => msg.id !== aiTempId));
              setIsLoading(false);
              setIsAudioLoading(false);
              break;
            default:
              break;
          }
        };

        const flushPublicFrame = (frame: { event?: string; data?: string }) => {
          if (!frame.event) return;
          let payload: unknown = {};
          if (frame.data) {
            try {
              payload = JSON.parse(frame.data);
            } catch {
              payload =
                frame.event === 'audio_chunk' ? { chunk: frame.data } : { content: frame.data };
            }
          }
          handleSSEEvent({ event: frame.event, data: payload });
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let idx: number;

          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            flushPublicFrame(parseSSELines(chunk));
          }
        }
      } catch (err) {
        setMessages((prev) => prev.filter((msg) => msg.id !== aiTempId));
        setIsLoading(false);

        if ((err as Error).name === 'AbortError') return;

        setError((err as Error).message);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [echoId, pageSlug, sessionId, ttsProvider]
  );

  const sendAudioMessage = React.useCallback(
    async (blob: Blob, waveform: number[], duration: number) => {
      setError(null);
      setIsLoading(true);

      const userTempId = `temp-audio-${Date.now()}`;
      const aiTempId = `temp-ai-${Date.now()}`;
      const localAudioUrl = URL.createObjectURL(blob);
      localAudioUrlsRef.current.add(localAudioUrl);

      // Optimistic UI updates
      const userMessage: Message = {
        id: userTempId,
        content: '',
        sender_type: MessageSender.USER,
        created_at: new Date().toISOString(),
        is_audio_message: true,
        localAudioUrl,
        waveform,
        duration,
        has_audio: false,
      };

      const aiPlaceholder: Message = {
        id: aiTempId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        isTextComplete: false,
        audioBuffer: [],
        has_audio: true,
      };

      setMessages((prev) => [...prev, userMessage, aiPlaceholder]);

      try {
        const formData = new FormData();
        formData.append('echo_id', echoId || '');
        formData.append('session_id', sessionId);
        formData.append('tts_provider', ttsProvider);
        formData.append('generate_audio', 'true');

        // STRICT FILE SANITIZATION (Exactly like chatService.ts)
        const rawType =
          typeof blob.type === 'string' && blob.type.length > 0 ? blob.type : 'audio/webm';
        const cleanType = rawType.split(';')[0];

        let extension = 'webm';
        if (cleanType === 'audio/mp4') extension = 'm4a';
        else if (cleanType === 'audio/mpeg') extension = 'mp3';
        else if (cleanType === 'audio/wav') extension = 'wav';
        else if (cleanType === 'audio/ogg') extension = 'ogg';

        const safeType: string = cleanType || 'audio/webm';
        const file = new File([blob], `voice_message.${extension}`, { type: safeType });
        formData.append('audio', file);

        const response = await fetch(`${BASE_URL}/chat/audio`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Upload failed (${response.status}): ${errText}`);
        }

        const data: AudioChatResponse = await response.json();

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === userTempId) {
              return {
                ...msg,
                id: data.user_message.id,
                content: data.user_message.content,
                ...(data.user_message.transcribed_text
                  ? { transcribed_text: data.user_message.transcribed_text }
                  : {}),
                localAudioUrl,
                waveform,
                duration,
              };
            }
            if (msg.id === aiTempId) {
              return {
                ...msg,
                id: data.ai_response.id,
                content: data.ai_response.content,
                isTextComplete: true,
                ...(data.ai_response.audio_url ? { audio_url: data.ai_response.audio_url } : {}),
                has_audio:
                  !!data.ai_response.audio_url && !(data.audio_generation_skipped ?? false),
                isAudioGenerated:
                  !!data.ai_response.audio_url && !(data.audio_generation_skipped ?? false),
                created_at: data.ai_response.created_at,
              };
            }
            return msg;
          })
        );
      } catch (err) {
        URL.revokeObjectURL(localAudioUrl);
        localAudioUrlsRef.current.delete(localAudioUrl);
        setError((err as Error).message);
        setMessages((prev) => prev.filter((msg) => msg.id !== userTempId && msg.id !== aiTempId));
      } finally {
        setIsLoading(false);
      }
    },
    [echoId, sessionId, ttsProvider]
  );

  // Audio playback functions
  const playAudio = React.useCallback((messageId: string, audioUrl: string) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setIsAudioLoading(true);
    setCurrentlyPlayingId(messageId);

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.oncanplaythrough = () => {
      setIsAudioLoading(false);
      audio.play().catch(() => {
        setCurrentlyPlayingId(null);
      });
    };

    audio.onended = () => {
      setCurrentlyPlayingId(null);
      audioRef.current = null;
    };

    audio.onerror = () => {
      setIsAudioLoading(false);
      setCurrentlyPlayingId(null);
      audioRef.current = null;
    };

    audio.load();
  }, []);

  const stopAudio = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCurrentlyPlayingId(null);
    setIsAudioLoading(false);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    sendAudioMessage,
    sessionId,
    echoInfo,
    isEchoLoading,
    playAudio,
    stopAudio,
    currentlyPlayingId,
    isAudioLoading,
  };
}
